import {
    format,
    parse
} from "date-fns";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState
} from "react";
import {
    IssueFilter,
    StatusFilter
} from "../../../lib/dashboard/types/IssueFilter";
import {
    Page,
    usePageNavigation
} from "./PageNavigationContextProvider";

const IssueFilterContext = createContext<{
    filter: IssueFilter;
    updateFilter: (filter: IssueFilter) => void;
} | undefined>(undefined);

const createFilterFromSearchParams = () => {
    const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const locationIds = searchParams.get("locationIds")?.split(",") as Array<string>;
    const issueId = searchParams.get("issueId");
    const statuses = searchParams.get("statuses")?.split(",") as StatusFilter[];
    const assessmentId = searchParams.get("assessmentId");
    const tagIds = searchParams.get("tagIds");
    return {
        date: {
            start: startDate ? parse(startDate, "yyyy-MM-dd", new Date()) : undefined,
            end: endDate ? parse(endDate!, "yyyy-MM-dd", new Date()) : undefined
        },
        locationIds: locationIds || undefined,
        issueId: issueId || undefined,
        statuses: statuses || undefined,
        assessmentId: assessmentId || undefined,
        tagIds: tagIds ? tagIds.split(",") : undefined
    } satisfies IssueFilter;
};

export const IssueFilterContextProvider = ({ children }: { children: ReactNode; }) => {
    const { navigateToPage } = usePageNavigation();
    const [filter, setFilter] = useState<IssueFilter>(createFilterFromSearchParams());

    useEffect(() => {
        const handlePopState = () => {
            setFilter(createFilterFromSearchParams());
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const updateFilter = (filter: IssueFilter) => {
        const url = new URL(window.location.href);
        let shouldPushState = false;
        // If the issueId or Assessment is changing, we treat it as a page navigation (Issue focus -> Issue list)
        if (url.searchParams.get("issueId") != filter.issueId || url.searchParams.get("assessmentId") != filter.assessmentId) {
            shouldPushState = true;
        }

        if (filter.date?.start) {
            url.searchParams.set("startDate", format(filter.date.start, "yyyy-MM-dd"));
        } else {
            url.searchParams.delete("startDate");
        }
        if (filter.date?.end) {
            url.searchParams.set("endDate", format(filter.date.end, "yyyy-MM-dd"));
        } else {
            url.searchParams.delete("endDate");
        }
        if (filter.locationIds) {
            url.searchParams.set("locationIds", filter.locationIds.join(","));
        } else {
            url.searchParams.delete("locationIds");
        }
        if (filter.issueId) {
            url.searchParams.set("issueId", filter.issueId);
        } else {
            url.searchParams.delete("issueId");
        }
        if (filter.statuses) {
            url.searchParams.set("statuses", filter.statuses.join(","));
        } else {
            url.searchParams.delete("statuses");
        }
        if (filter.assessmentId) {
            url.searchParams.set("assessmentId", filter.assessmentId);
        } else {
            url.searchParams.delete("assessmentId");
        }
        if (filter.tagIds) {
            url.searchParams.set("tagIds", filter.tagIds.join(","));
        } else {
            url.searchParams.delete("tagIds");
        }
        setFilter(filter);
        navigateToPage(Page.Issues, url.searchParams, !shouldPushState);
    };

    return (
        <IssueFilterContext.Provider value={{ filter, updateFilter }}>
            {children}
        </IssueFilterContext.Provider>
    );
};

export const useIssueFilter = () => {
    const context = useContext(IssueFilterContext);
    if (!context) {
        throw new Error("useIssueFilter must be used within an IssueFilterProvider");
    }
    return context;
};
